import {
  createDatabaseBindings,
  Failure,
  getId,
  maybeAppendCopyToText,
  ModernIDB,
  Success,
  type DatabaseAppMachineMethods,
} from "@indietabletop/appkit";
import type { SpaceGits, UserGameData } from "@indietabletop/types";
import type { Ref } from "./engine/common.ts";
import { Gang } from "./engine/Gang.ts";
import { initDatabase, migrateDatabase } from "./migrations.ts";

export type GangNotFound = {
  type: "GANG_NOT_FOUND";
};

export type SettingsNotFound = {
  type: "SETTINGS_NOT_FOUND";
};

class GangsStore {
  private db: SpaceGitsIDB;

  constructor(db: SpaceGitsIDB) {
    this.db = db;
  }

  private async putOne(data: SpaceGits.GangData | SpaceGits.GangTombstone) {
    const key = await this.db.putToStore("gangs", data);

    // We know that our keys are strings
    return new Success(key as string);
  }

  async createOne(
    data: Pick<
      Partial<SpaceGits.GangData>,
      "gits" | "vehicles" | "description" | "faction"
    > &
      Pick<SpaceGits.GangData, "name" | "mode" | "rulesetVersion">,
  ) {
    return await this.putOne(Gang.initData(data));
  }

  async duplicateOne(gang: Gang) {
    const now = Date.now();

    return await this.putOne({
      ...gang.data,

      id: getId(),
      name: maybeAppendCopyToText(gang.data.name),
      createdTs: now,
      updatedTs: now,
      snapshotId: null,
    });
  }

  async getOne(id: string) {
    const gang = await this.db.getFromStore("gangs", id);

    if (!gang || gang.deleted) {
      return new Failure<GangNotFound>({ type: "GANG_NOT_FOUND" });
    }

    return new Success(gang);
  }

  async getAll() {
    const [tx, done] = this.db.transaction("gangs", "readonly");
    const [gangs] = await Promise.all([tx.index("createdTs").getAll(), done]);

    return new Success(gangs.filter((gang) => !gang.deleted));
  }

  async updateOne(
    data: SpaceGits.GangData,
    options?: { allowSetSnapshotId?: boolean },
  ) {
    return this.putOne({
      ...data,

      // Always reset snapshotId if a change is made unless explicitly allowed.
      snapshotId: options?.allowSetSnapshotId ? data.snapshotId : null,
      updatedTs: Date.now(),
    });
  }

  /**
   * Sets snapshot ID.
   *
   * This is the only way to set the snapshot ID, as all other public update
   * methods reset the snapshot ID to `null`.
   */
  async setSnapshotId(gang: Gang, snapshotId: string) {
    return this.updateOne(
      { ...gang.data, snapshotId },
      { allowSetSnapshotId: true },
    );
  }

  async patchOne(gang: Gang, data: Partial<SpaceGits.GangData>) {
    return this.updateOne({ ...gang.data, ...data });
  }

  async setFaction(gang: Gang, faction: Ref | null) {
    return this.updateOne({
      ...gang.data,
      faction,
    });
  }

  async deleteOne(gang: Gang) {
    const { data } = gang;
    return this.putOne({
      id: data.id,
      name: data.name,
      deleted: true,
      updatedTs: Date.now(),
    });
  }
}

type Settings = SpaceGits.PrintSettings | SpaceGits.Onboarding;

class SettingsStore {
  db: SpaceGitsIDB;

  constructor(db: SpaceGitsIDB) {
    this.db = db;
  }

  private async putOne(data: Settings) {
    const key = await this.db.putToStore("settings", data);

    // We know that our keys are strings
    return new Success(key as string);
  }

  async upsertOne<T extends Settings["id"]>(
    data: { id: T } & Extract<Settings, { id: T }>,
  ) {
    return await this.putOne({ ...data, updatedTs: Date.now() });
  }

  async getPrintSettings() {
    type RequestedSettings = Extract<typeof settings, { id: typeof id }>;
    const id: SpaceGits.PrintSettings["id"] = "PRINT_SETTINGS";
    const settings = await this.db.getFromStore("settings", id);

    // If not found, return a fully initialized print settings
    if (!settings) {
      return new Success<RequestedSettings>({
        id,
        printGitRules: true,
        printVehicleRules: true,
        printWeaponRules: true,
        updatedTs: Date.now(),
      });
    }

    return new Success(settings as RequestedSettings);
  }

  async removeOnboardingSettings() {
    const id: SpaceGits.Onboarding["id"] = "ONBOARDING";
    await this.db.deleteFromStore("settings", id);

    return new Success(null);
  }

  async getOnboardingSettings() {
    type RequestedSettings = Extract<typeof settings, { id: typeof id }>;
    const id: SpaceGits.Onboarding["id"] = "ONBOARDING";
    const settings = await this.db.getFromStore("settings", id);

    if (!settings) {
      const now = Date.now();

      return new Success<RequestedSettings>({
        id,
        onboardedTopics: {},
        updatedTs: now,
        createdTs: now,
      });
    }

    return new Success(settings as RequestedSettings);
  }
}

export type SpaceGitsSchema = {
  gangs: SpaceGits.GangData | SpaceGits.GangTombstone;
  settings: SpaceGits.PrintSettings | SpaceGits.Onboarding;
};

export type SpaceGitsIndexes = {
  gangs: "createdTs" | "updatedTs" | "rulesetVersion";
  settings: "updatedTs";
};

type SpaceGitsIDB = ModernIDB<SpaceGitsSchema, SpaceGitsIndexes>;

type SyncedData = {
  spacegits: {
    gangs: (SpaceGits.GangData | SpaceGits.GangTombstone)[];
    settings: (SpaceGits.PrintSettings | SpaceGits.Onboarding)[];
  };
};

export class SpaceGitsDatabase
  extends ModernIDB<SpaceGitsSchema, SpaceGitsIndexes>
  implements DatabaseAppMachineMethods<SyncedData>
{
  gangs: GangsStore;
  settings: SettingsStore;

  constructor() {
    super({
      name: "spacegits",
      version: 2,
      onInit: initDatabase,
      onUpgrade: migrateDatabase,
    });

    this.gangs = new GangsStore(this);
    this.settings = new SettingsStore(this);
  }

  async getUpdatedGameDataSince(params: {
    sinceTs: number | null;
    exclude: Set<string>;
  }) {
    const { sinceTs, exclude } = params;

    const [[gangsStore, settingsStore], done] = this.transaction([
      "gangs",
      "settings",
    ]);
    const gangsIdx = gangsStore.index("updatedTs");
    const settingsIdx = settingsStore.index("updatedTs");

    // If a timestamp is provided, we use it as the lower bound, getting all
    // items that were updated after the specified time. Otherwise we use an
    // unbounded range by using `null` (retrieves all items).
    const range = sinceTs && IDBKeyRange.lowerBound(sinceTs);

    const [gangs, settings] = await Promise.all([
      gangsIdx.getAll(range),
      settingsIdx.getAll(range),
      done,
    ]);

    return new Success({
      spacegits: {
        gangs: gangs.filter((gang) => !exclude.has(gang.id)),
        settings: settings,
      },
    });
  }

  async upsertGameData(data: UserGameData) {
    const { spacegits: { gangs = [], settings = [] } = {} } = data;

    if (gangs.length + settings.length === 0) {
      return new Success([]);
    }

    const [[gangsStore, settingsStore], done] = this.transaction(
      ["gangs", "settings"],
      "readwrite",
    );
    const [gangsKeys, settingsKeys] = await Promise.all([
      Promise.all(gangs.map((gang) => gangsStore.put(gang))),
      Promise.all(settings.map((setting) => settingsStore.put(setting))),
      done,
    ]);

    return new Success([...gangsKeys, ...settingsKeys] as string[]);
  }

  /**
   * This method should perform a complete cleanup of IDB.
   *
   * It should be used for when a user is logging out, or when a logged-out
   * user is explicitly choosing to delete all data.
   */
  async clearAll() {
    // Using a record to make sure TS guarantees that all stores are included
    const record: Record<keyof SpaceGitsSchema, true> = {
      gangs: true,
      settings: true,
    };

    const [stores, done] = this.transaction(
      Object.keys(record) as Array<keyof SpaceGitsSchema>,
      "readwrite",
    );

    await Promise.all([...stores.map((store) => store.clear()), done]);

    return new Success("Cleared.");
  }
}

export const database = new SpaceGitsDatabase();

Object.assign(window, { spacegits: database });

export const {
  useQuery,
  useDatabase,
  useDatabaseOpenRequest,
  DatabaseProvider,
} = createDatabaseBindings(database);
